/**
 * React wrapper around a ProseMirror EditorView. The page-shaped writing
 * surface, indents, spacing, and allcaps rendering all come from a scoped
 * stylesheet generated from the document's own settings + styles (metrics.ts).
 */
import { useEffect, useRef } from "react";
import { EditorView } from "prosemirror-view";
import type { EditorState } from "prosemirror-state";
import type { DocumentSession } from "../app/session";
import { metricsCss } from "./metrics";
import { currentStyleName, elementEnter } from "./commands";
import { acceptActiveSuggestion } from "./autocomplete";
import "./editor.css";

export interface EditorChangeInfo {
  elementType: string | null;
  dirty: boolean;
}

export interface EditorSurfaceProps {
  session: DocumentSession;
  /** Receives the live EditorView so the app chrome can run commands on it. */
  viewRef?: React.MutableRefObject<EditorView | null>;
  onChange?: (info: EditorChangeInfo) => void;
  /** Fired only when the document content actually changed (for autosave). */
  onDocChanged?: () => void;
  /** Page zoom factor (1 = 100%). */
  zoom?: number;
}

const SCOPE = "sp-page";

export function EditorSurface({
  session,
  viewRef,
  onChange,
  onDocChanged,
  zoom = 1,
}: EditorSurfaceProps) {
  const pageRef = useRef<HTMLDivElement | null>(null);
  const styleRef = useRef<HTMLStyleElement | null>(null);
  // Keep the latest callbacks without re-creating the view.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onDocChangedRef = useRef(onDocChanged);
  onDocChangedRef.current = onDocChanged;

  useEffect(() => {
    const mount = pageRef.current;
    if (!mount) return;

    // Inject the document-specific layout stylesheet.
    const styleEl = document.createElement("style");
    styleEl.textContent = metricsCss(session.metrics, `.${SCOPE}`);
    document.head.appendChild(styleEl);
    styleRef.current = styleEl;

    const notify = (state: EditorState) => {
      onChangeRef.current?.({
        elementType: currentStyleName(state),
        dirty: session.dirty,
      });
    };

    const view = new EditorView(mount, {
      state: session.createState(),
      attributes: { lang: "sr-Latn", spellcheck: "false" },
      dispatchTransaction(tr) {
        const newState = view.state.apply(tr);
        view.updateState(newState);
        if (tr.docChanged) {
          session.markDirty();
          onDocChangedRef.current?.();
        }
        notify(newState);
      },
      handleDOMEvents: {
        // Some Android IMEs fire beforeinput without a keydown for Enter.
        beforeinput: (v, event) => {
          const e = event as InputEvent;
          if (e.inputType === "insertParagraph" || e.inputType === "insertLineBreak") {
            // If the autocomplete dropdown is open, Enter accepts it; otherwise
            // run the element flow. Either way we consume the beforeinput.
            const handled =
              acceptActiveSuggestion(v) || elementEnter(session.styles)(v.state, v.dispatch, v);
            if (handled) {
              e.preventDefault();
              return true;
            }
          }
          return false;
        },
      },
    });

    if (viewRef) viewRef.current = view;
    notify(view.state);

    // When the soft keyboard opens/resizes the viewport, keep the caret visible.
    const vv = window.visualViewport;
    const onViewportResize = () => {
      if (!view.isDestroyed) view.dispatch(view.state.tr.scrollIntoView());
    };
    vv?.addEventListener("resize", onViewportResize);

    return () => {
      vv?.removeEventListener("resize", onViewportResize);
      view.destroy();
      if (viewRef) viewRef.current = null;
      styleEl.remove();
      styleRef.current = null;
    };
    // Re-create only when the session (document) changes.
  }, [session, viewRef]);

  return (
    <div className="sp-desk" style={{ ["--sp-zoom" as string]: String(zoom) }}>
      <div ref={pageRef} className={SCOPE} />
    </div>
  );
}
